<template>
	<div>
		<!-- 使用自定义对话框组件 -->
		<non-modal-dialog :visible="showRoom" :title="title" :width="windowSize.width" :height="windowSize.height"
			:taskbar-status="taskbarStatusText" :show-maximize="false" @close="onQuit">

			<!-- 任务栏头像插槽 -->
			<template #taskbar-avatar>
				<head-image :size="40" :name="friendName" :url="friend.headImage" radius="50%"></head-image>
			</template>

			<!-- 对话框内容 -->
			<div class="rtc-private-video" @click="resumePlayback">
				<div class="rtc-session-strip">
					<div class="session-meta">
						<head-image :size="36" :name="friendName" :url="friend.headImage" :isShowUserInfo="false" radius="50%"></head-image>
						<div class="session-copy">
							<div class="session-name">{{ friendName }}</div>
							<div class="session-hint">{{ rtcHintText }}</div>
						</div>
					</div>
					<div class="session-chip" :class="{ 'is-active': isChating }">{{ rtcStateText }}</div>
				</div>
				<div v-show="isVideo" class="rtc-video-box">
					<div class="rtc-video-friend">
						<div v-if="!remoteStream" class="rtc-stage-placeholder">
							<head-image :size="92" :name="friendName" :url="friend.headImage" :isShowUserInfo="false" radius="50%"></head-image>
							<div class="placeholder-title">{{ friendName }}</div>
							<div class="placeholder-text">{{ remoteStageHintText }}</div>
						</div>
						<video ref="remoteVideo" autoplay=""></video>
					</div>
					<div class="rtc-video-mine">
						<div v-if="!localPreviewReady" class="rtc-mine-placeholder">
							<span class="placeholder-dot"></span>
							<span>{{ localStream ? '正在获取本地画面' : '本地画面准备中' }}</span>
						</div>
						<video v-show="localPreviewReady" ref="localVideo" autoplay=""></video>
					</div>
				</div>
				<div v-show="!isVideo" class="rtc-voice-box">
					<div class="voice-card">
						<div class="voice-avatar-shell">
							<head-image class="friend-head-image" :size="168" :name="friendName"
								:url="friend.headImage" :isShowUserInfo="false"></head-image>
						</div>
						<div class="rtc-voice-name">{{ friendName }}</div>
						<div class="rtc-voice-status">{{ rtcHintText }}</div>
					</div>
				</div>
				<div class="rtc-control-bar">
					<button type="button" class="rtc-action-btn rtc-action-btn--danger" title="结束通话" @click="onQuit()">
						<i class="icon iconfont icon-phone-reject"></i>
						<span>{{ quitLabel }}</span>
					</button>
				</div>
			</div>
		</non-modal-dialog>
		<rtc-private-acceptor v-if="!isHost && isWaiting" ref="acceptor" :friend="friend" :mode="mode"
			@accept="onAccept" @reject="onReject"></rtc-private-acceptor>
	</div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import RtcPrivateAcceptor from './RtcPrivateAcceptor.vue';
import NonModalDialog from '../common/NonModalDialog.vue';
import RtcPrivateApi from '@/api/rtcPrivateApi'
import RtcMediaSession from '@/api/rtcMediaSession'
import RtcRingtonePlayer from '@/utils/rtcRingtonePlayer'
import { hasUsableVideoFrame } from '@/utils/rtcVideoPreview'
import { logRtcEvent } from '@/utils/rtcLogger';

export default {
	name: 'rtcPrivateVideo',
	components: {
		HeadImage,
		RtcPrivateAcceptor,
		NonModalDialog
	},
	data() {
		return {
			API: new RtcPrivateApi(), // API
			media: null, // ZEGO媒体会话，实例不参与业务状态管理
			ringtonePlayer: null,
			audio: null, // 呼叫音频
			isRinging: false,
			showRoom: false,
			friend: {},
			isHost: false, // 是否发起人
			state: "CLOSE", // CLOSE:关闭  WAITING:等待呼叫或接听 CHATING:聊天中  ERROR:出现异常
			mode: 'video', // 模式 video:视频聊 voice:语音聊天
			localStream: null, // 本地视频流
			remoteStream: null, // 对方视频流
			callId: null,
			pendingSignals: [],
			joiningMedia: false,
			videoTime: 0,
			videoTimer: null,
			heartbeatTimer: null,
			callGeneration: 0,
			callStartedAt: null,
			peerAccepted: false,
			callNoticeShown: false,
			callEnded: true,
			localPreviewRetryTimer: null,
			localPreviewRetryCount: 0,
			localPreviewReady: false,
			localPreviewTracks: []
		}
	},
	methods: {
		resumePlayback() {
			this.retryRingtone();
			const video = this.$refs.remoteVideo;
			if (video && this.remoteStream) video.play().catch(() => {});
		},
		getFriendId() {
			return this.friend && this.friend.id;
		},
		attachRemoteStream(stream) {
			this.$nextTick(() => {
				const remoteVideo = this.$refs.remoteVideo;
				if (!remoteVideo || stream !== this.remoteStream) {
					return;
				}
				remoteVideo.srcObject = stream;
				if (stream) remoteVideo.play().catch(() => this.$message.warning('请点击通话窗口以允许播放远端音视频'));
			});
		},
		attachLocalStream(stream) {
			const generation = this.callGeneration;
			this.clearLocalPreviewMonitor(false);
			this.$nextTick(() => {
				const localVideo = this.$refs.localVideo;
				if (!localVideo || stream !== this.localStream) {
					if (stream === this.localStream && this.isCurrentCall(generation)) {
						this.recordMediaDiagnostic({ event: 'local-preview-element-missing' });
						this.localPreviewRetryTimer = setTimeout(() => {
							this.localPreviewRetryTimer = null;
							this.attachLocalStream(stream);
						}, 32);
					}
					return;
				}
				const videoTracks = stream && stream.getVideoTracks ? stream.getVideoTracks() : [];
				if (!videoTracks.length) {
					localVideo.srcObject = null;
					this.localPreviewReady = false;
					return;
				}
				localVideo.srcObject = stream;
				localVideo.muted = true;
				localVideo.autoplay = true;
				localVideo.playsInline = true;
				this.localPreviewReady = false;
				this.localPreviewRetryCount = 0;
				['loadeddata', 'canplay', 'playing', 'resize', 'timeupdate'].forEach(eventName => {
					localVideo[`on${eventName}`] = () => this.updateLocalPreview(generation, stream);
				});
				this.localPreviewTracks = videoTracks;
				videoTracks.forEach(track => {
					if (!track.addEventListener) return;
					track.addEventListener('ended', this.onLocalPreviewTrackStateChange);
					track.addEventListener('mute', this.onLocalPreviewTrackStateChange);
					track.addEventListener('unmute', this.onLocalPreviewTrackStateChange);
				});
				Promise.resolve(localVideo.play()).then(() => {
					this.updateLocalPreview(generation, stream);
					if (!this.localPreviewReady) this.scheduleLocalPreviewCheck(generation, stream, true);
				}).catch(error => {
					if (!this.isCurrentCall(generation) || stream !== this.localStream || error?.name === 'AbortError') return;
					this.recordMediaDiagnostic({ event: 'local-preview-play-failed', errorName: error && error.name || 'PlaybackError' });
					this.scheduleLocalPreviewCheck(generation, stream, true);
				});
				this.scheduleLocalPreviewCheck(generation, stream, false);
			});
		},
		updateLocalPreview(generation = this.callGeneration, stream = this.localStream) {
			if (!this.isCurrentCall(generation) || stream !== this.localStream) return;
			const video = this.$refs.localVideo;
			const ready = hasUsableVideoFrame(video, stream);
			if (ready && !this.localPreviewReady) {
				this.recordMediaDiagnostic({
					event: 'local-preview-first-frame',
					videoWidth: video.videoWidth,
					videoHeight: video.videoHeight,
					trackStates: stream.getVideoTracks().map(track => track.readyState)
				});
			}
			this.localPreviewReady = ready;
			if (ready && this.localPreviewRetryTimer) {
				clearTimeout(this.localPreviewRetryTimer);
				this.localPreviewRetryTimer = null;
			}
		},
		onLocalPreviewTrackStateChange() {
			this.updateLocalPreview(this.callGeneration, this.localStream);
		},
		scheduleLocalPreviewCheck(generation, stream, retryPlayback) {
			if (this.localPreviewRetryTimer || this.localPreviewRetryCount >= 40) return;
			this.localPreviewRetryCount += 1;
			this.localPreviewRetryTimer = setTimeout(() => {
				this.localPreviewRetryTimer = null;
				if (!this.isCurrentCall(generation) || stream !== this.localStream) return;
				this.updateLocalPreview(generation, stream);
				if (this.localPreviewReady || this.localPreviewRetryCount >= 40) return;
				const video = this.$refs.localVideo;
				if (video && (retryPlayback || video.paused)) Promise.resolve(video.play()).catch(() => {});
				this.scheduleLocalPreviewCheck(generation, stream, true);
			}, 100);
		},
		clearLocalPreviewMonitor(clearSource = true) {
			if (this.localPreviewRetryTimer) clearTimeout(this.localPreviewRetryTimer);
			this.localPreviewRetryTimer = null;
			this.localPreviewRetryCount = 0;
			this.localPreviewReady = false;
			this.localPreviewTracks.forEach(track => {
				if (!track.removeEventListener) return;
				track.removeEventListener('ended', this.onLocalPreviewTrackStateChange);
				track.removeEventListener('mute', this.onLocalPreviewTrackStateChange);
				track.removeEventListener('unmute', this.onLocalPreviewTrackStateChange);
			});
			this.localPreviewTracks = [];
			const video = this.$refs.localVideo;
			if (!video) return;
			['loadeddata', 'canplay', 'playing', 'resize', 'timeupdate'].forEach(eventName => {
				video[`on${eventName}`] = null;
			});
			if (clearSource) video.srcObject = null;
		},
		handleBeforeUnload() {
			this.onQuit();
		},
		open(rtcInfo) {
			this.close();
			this.callStartedAt = Date.now();
			this.peerAccepted = false;
			this.callNoticeShown = false;
			this.callEnded = false;
			const sessionInfo = rtcInfo || {};
			this.showRoom = true;
			this.userStore.setInRtc(true);
			this.mode = sessionInfo.mode || 'video';
			this.isHost = Boolean(sessionInfo.isHost);
			this.recordMediaDiagnostic({ event: 'call-opened', isHost: this.isHost, mode: this.mode });
			this.friend = sessionInfo.friend || {
				id: sessionInfo.friendId || null
			};
			if (this.isHost) {
				this.startRingtone();
				this.onCall();
			}
		},
		initAudio() {
			if (!this.ringtonePlayer) {
				this.ringtonePlayer = new RtcRingtonePlayer({
					source: require('@/assets/audio/call.wav'),
					AudioCtor: typeof Audio === 'function' ? Audio : null,
					onEvent: event => this.recordMediaDiagnostic(event),
					onStateChange: value => { this.isRinging = value; }
				});
			}
			this.audio = this.ringtonePlayer.init();
			return this.audio;
		},
		startRingtone() {
			this.initAudio();
			if (this.ringtonePlayer) this.ringtonePlayer.start();
		},
		retryRingtone() {
			if (!this.isWaiting) return;
			this.recordMediaDiagnostic({ event: 'ringtone-play-retry' });
			if (this.ringtonePlayer) this.ringtonePlayer.retry();
			else this.startRingtone();
		},
		stopRingtone() {
			if (this.ringtonePlayer) this.ringtonePlayer.stop();
		},
		recordMediaDiagnostic(event) {
			const details = {
				callElapsedMs: this.callStartedAt ? Date.now() - this.callStartedAt : null,
				...event
			};
			logRtcEvent('private', details.event || 'diagnostic', details);
		},
		isCurrentCall(generation) {
			return generation === this.callGeneration && !this.isClose;
		},
		failCall(error, generation = this.callGeneration) {
			if (!this.isCurrentCall(generation) || error?.name === 'AbortError') return;
			const unanswered = !this.peerAccepted && this.isWaiting;
			const reason = unanswered ? (this.isHost ? '对方未接听' : null) : (error?.message || '通话未能建立，请稍后重试');
			console.warn('[RTC]', { flow: 'private', event: 'call-failed', errorName: error && error.name || 'Error', errorCode: Number.isInteger(error && error.code) ? error.code : null, status: error && error.response && error.response.status || null });
			this.recordMediaDiagnostic({ event: unanswered && !this.isHost ? 'call-unanswered-silent' : 'call-failed', userMessage: reason, unanswered });
			// 未接听的被叫只静默收尾；已经接听但设备/媒体失败时仍需通知呼叫方。
			if ((!unanswered || this.isHost) && this.callId && this.getFriendId()) {
				this.API.failed(this.callId, this.getFriendId(), error?.message || 'media-failed').catch(() => {});
			}
			this.close(reason, unanswered ? 'warning' : 'error');
		},
		initMedia(generation) {
			this.media = new RtcMediaSession({
				readyWhen: 'remote-playing',
				getCredentials: callId => this.API.credentials(callId, this.getFriendId()),
				onLocalStream: stream => {
					if (!this.isCurrentCall(generation)) return;
					this.localStream = stream;
					this.attachLocalStream(stream);
				},
				onDiagnostic: event => {
					if (this.isCurrentCall(generation)) this.recordMediaDiagnostic(event);
				},
				onRemoteStream: (userId, stream) => {
					if (!this.isCurrentCall(generation)) return;
					this.stopRingtone();
					this.remoteStream = stream;
					this.attachRemoteStream(stream);
				},
				onRemoteRemoved: () => {
					if (this.isCurrentCall(generation)) this.remoteStream = null;
				},
				onState: state => {
					if (!this.isCurrentCall(generation)) return;
					if (state === 'connected') this.state = 'CHATING';
					if (state === 'reconnecting') this.state = 'CONNECTING';
				},
				onMediaReady: () => {
					if (!this.isCurrentCall(generation) || !this.callId) return;
					this.recordMediaDiagnostic({ event: 'business-ready-requested' });
					this.API.ready(this.callId, this.getFriendId()).then(() => {
						if (this.isCurrentCall(generation)) {
							this.recordMediaDiagnostic({ event: 'business-ready-succeeded' });
							this.state = 'CHATING';
							this.startChatTime();
						}
					}).catch(error => {
						if (!this.isCurrentCall(generation)) return;
						this.recordMediaDiagnostic({ event: 'business-ready-failed', status: error && error.response && error.response.status || null });
						this.failCall(error, generation);
					});
				},
				onRemoteError: error => this.failCall(error, generation),
				onError: error => this.failCall(error, generation)
			});
		},
		async onCall() {
			if (!this.getFriendId()) {
				this.close();
				return;
			}
			const generation = this.callGeneration;
			this.state = 'STARTING';
			this.initMedia(generation);
			const media = this.media;
			try {
				const friendId = this.getFriendId();
				this.recordMediaDiagnostic({ event: 'business-setup-requested' });
				// 业务呼叫和本地设备采集并行，摄像头产生视频轨后立即显示本地预览。
				const setupPromise = this.API.setup(friendId, this.mode).then(result => {
					if (!this.isCurrentCall(generation)) {
						this.API.cancel(result.callId, friendId).catch(() => {});
						return result;
					}
					this.callId = result.callId;
					this.recordMediaDiagnostic({ event: 'business-setup-succeeded' });
					this.state = 'WAITING';
					this.startHeartBeat();
					this.startRingtone();
					return result;
				});
				const capturePromise = media.prepare(this.mode);
				await Promise.all([setupPromise, capturePromise]);
				if (!this.isCurrentCall(generation)) return;
				const pending = this.pendingSignals;
				this.pendingSignals = [];
				pending.forEach(msg => this.onRTCMessage(msg));
			} catch (error) {
				this.failCall(error, generation);
			}
		},
		async onAccept() {
			if (!this.isWaiting) return;
			if (!this.getFriendId() || !this.callId) {
				this.close();
				return;
			}
			const generation = this.callGeneration;
			this.state = 'ACQUIRING';
			this.showRoom = true;
			this.userStore.setInRtc(true);
			this.stopRingtone();
			this.initMedia(generation);
			const media = this.media;
			const callId = this.callId;
			const friendId = this.getFriendId();
			try {
				this.state = 'CONNECTING';
				this.recordMediaDiagnostic({ event: 'business-accept-requested' });
				// 接听确认和本地设备采集并行，设备准备完成后即可显示本地预览。
				const acceptPromise = this.API.accept(callId, friendId).then(() => {
					if (!this.isCurrentCall(generation)) {
						this.API.handup(callId, friendId).catch(() => {});
						return false;
					}
					this.recordMediaDiagnostic({ event: 'business-accept-succeeded' });
					return true;
				});
				const capturePromise = media.prepare(this.mode);
				const [accepted] = await Promise.all([acceptPromise, capturePromise]);
				if (!accepted) return;
				if (!this.isCurrentCall(generation)) return;
				await this.joinMedia(generation);
			} catch (error) {
				this.failCall(error, generation);
			}
		},
		async joinMedia(generation) {
			if (!this.isCurrentCall(generation) || this.joiningMedia) return;
			this.joiningMedia = true;
			const media = this.media;
			this.state = 'CONNECTING';
			this.stopRingtone();
			this.recordMediaDiagnostic({ event: 'credentials-requested' });
			const credentials = await this.API.credentials(this.callId, this.getFriendId());
			if (!this.isCurrentCall(generation)) return;
			this.recordMediaDiagnostic({ event: 'credentials-received', expiresInSeconds: Number.isFinite(credentials && credentials.expiresAt) ? Math.max(0, Math.round(credentials.expiresAt - Date.now() / 1000)) : null });
			await media.join(credentials);
		},
		onReject() {
			if (this.getFriendId() && this.callId) {
				this.API.reject(this.callId, this.getFriendId());
			}
			// 退出
			this.close();
		},
		onHandup() {
			if (this.getFriendId() && this.callId) {
				this.API.handup(this.callId, this.getFriendId())
			}
			this.$message.success("您已挂断,通话结束")
			this.close();
		},
		onCancel() {
			if (this.getFriendId() && this.callId) {
				this.API.cancel(this.callId, this.getFriendId())
			}
			this.$message.success("已取消呼叫,通话结束")
			this.close();
		},
		onRTCMessage(msg) {
			// 业务信令可能先于 setup 的 HTTP 响应到达，取得本次 callId 后再匹配处理。
			if (this.isHost && this.state === 'STARTING' && !this.callId &&
				[this.$enums.MESSAGE_TYPE.RTC_ACCEPT, this.$enums.MESSAGE_TYPE.RTC_REJECT,
				 this.$enums.MESSAGE_TYPE.RTC_FAILED, this.$enums.MESSAGE_TYPE.RTC_CANCEL,
				 this.$enums.MESSAGE_TYPE.RTC_HANDUP].includes(msg.type) && msg.callId &&
				(msg.selfSend || String(msg.sendId) === String(this.getFriendId()))) {
				this.pendingSignals.push(msg);
				return;
			}
			// 除了发起通话，如果在关闭状态就无需处理
			if (msg.type != this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE &&
				msg.type != this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO &&
				this.isClose) {
				return;
			}
			if (msg.type !== this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE &&
				msg.type !== this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO && !msg.selfSend &&
				String(msg.sendId) !== String(this.getFriendId())) return;
			if (msg.type !== this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE &&
				msg.type !== this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO &&
				(!msg.callId || msg.callId !== this.callId)) return;
			// RTC信令处理
			switch (msg.type) {
				case this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE:
					this.onRTCSetup(msg, 'voice')
					break;
				case this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO:
					this.onRTCSetup(msg, 'video')
					break;
				case this.$enums.MESSAGE_TYPE.RTC_ACCEPT:
					this.onRTCAccept(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_REJECT:
					this.onRTCReject(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_CANCEL:
					this.onRTCCancel(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_FAILED:
					this.onRTCFailed(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_HANDUP:
					this.onRTCHandup(msg)
					break;
			}
		},
		onRTCSetup(msg, mode) {
			if (!this.isClose) return;
			if (this.userStore.isInRtc) {
				this.API.failed(msg.callId, msg.sendId, '正在通话中').catch(() => {});
				return;
			}
			this.callGeneration++;
			this.callStartedAt = Date.now();
			this.peerAccepted = false;
			this.callNoticeShown = false;
			this.callEnded = false;
			this.isHost = false;
			this.mode = mode;
			this.callId = msg.callId;
			this.recordMediaDiagnostic({ event: 'signal-setup-received', mode });
			if (!this.callId) return;
			this.friend = {
				id: msg.sendId,
				nickName: '当前联系人'
			};
			this.state = "WAITING";
			this.userStore.setInRtc(true);
			this.startRingtone();
			this.startHeartBeat();
			this.$http({
				url: `/friend/find/${msg.sendId}`,
				method: 'get'
			}).then((friend) => {
				if (!this.isClose && String(this.getFriendId()) === String(msg.sendId)) this.friend = friend || this.friend;
			})
		},
		onRTCAccept(msg) {
			if (!msg.callId || msg.callId !== this.callId) return;
			this.recordMediaDiagnostic({ event: 'signal-accept-received', selfSend: Boolean(msg.selfSend) });
			if (!msg.selfSend && this.joiningMedia) return;
			if (msg.selfSend) {
				// 在其他设备接听
				this.$message.success("已在其他设备接听");
				this.close();
			} else {
				this.peerAccepted = true;
				const generation = this.callGeneration;
				this.state = 'CONNECTING';
				this.stopRingtone();
				this.joinMedia(generation)
					.catch(error => this.failCall(error, generation));
			}
		},
		onRTCReject(msg) {
			this.recordMediaDiagnostic({ event: 'signal-reject-received', selfSend: Boolean(msg.selfSend) });
			if (msg.selfSend) {
				this.$message.success("已在其他设备拒绝");
				this.close();
			} else {
				this.$message.error("对方拒绝了您的通话请求");
				this.close();
			}
		},
		onRTCFailed(msg) {
			this.recordMediaDiagnostic({ event: 'signal-failed-received' });
			// 呼叫失败
			const serverReason = String(msg.content || '').slice(0, 80);
			const unanswered = !this.peerAccepted && this.isWaiting;
			const userMessage = unanswered && this.isHost ? '对方未接听' : (unanswered ? null : '通话未能建立，请稍后重试');
			this.recordMediaDiagnostic({ event: 'business-call-failed', serverReason, userMessage, unanswered });
			this.close(userMessage, unanswered ? 'warning' : 'error');

		},
		onRTCCancel() {
			this.recordMediaDiagnostic({ event: 'signal-cancel-received' });
			// 对方取消通话
			this.close('对方取消了呼叫', 'info');
		},
		onRTCHandup() {
			this.recordMediaDiagnostic({ event: 'signal-handup-received' });
			// 对方挂断
			this.close('对方已挂断', 'info');
		},
		startChatTime() {
			this.videoTime = 0;
			this.videoTimer && clearInterval(this.videoTimer);
			this.videoTimer = setInterval(() => {
				this.videoTime++;
			}, 1000)
		},
		startHeartBeat() {
			// 每15s推送一次心跳
			this.heartbeatTimer && clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = setInterval(() => {
				if (!this.getFriendId() || !this.callId) {
					return;
				}
				const generation = this.callGeneration;
				this.API.heartbeat(this.callId, this.getFriendId()).then(() => {
					if (this.isCurrentCall(generation)) this.recordMediaDiagnostic({ event: 'heartbeat-succeeded' });
				}).catch(error => {
					if (!this.isCurrentCall(generation)) return;
					this.recordMediaDiagnostic({ event: 'heartbeat-failed', errorName: error && error.name || 'Error', status: error && error.response && error.response.status || null });
					this.failCall(error, generation);
				});
			}, 15000)
		},
		showCallNotice(message, type = 'info') {
			if (!message || this.callNoticeShown) return;
			this.callNoticeShown = true;
			this.$message({ message, type });
		},
		close(message, type = 'info') {
			if (this.callEnded && this.isClose) return;
			this.callEnded = true;
			const wasActive = !this.isClose;
			this.callGeneration++;
			this.showCallNotice(message, type);
			this.showRoom = false;
			if (wasActive) this.userStore.setInRtc(false);
			if (this.media) this.media.close();
			this.stopRingtone();
			this.videoTime = 0;
			this.videoTimer && clearInterval(this.videoTimer);
			this.heartbeatTimer && clearInterval(this.heartbeatTimer);
			this.videoTimer = null;
			this.heartbeatTimer = null;
			this.clearLocalPreviewMonitor();
			this.state = 'CLOSE';
			this.localStream = null;
			this.remoteStream = null;
			this.media = null;
			this.callId = null;
			this.callStartedAt = null;
			this.pendingSignals = [];
			this.joiningMedia = false;
			this.peerAccepted = false;
			this.friend = {};
		},
		onQuit() {
			if (this.isChating || this.state === 'CONNECTING') {
				this.onHandup();
			} else if (this.isWaiting || this.state === 'ACQUIRING') {
				this.isHost ? this.onCancel() : this.onReject();
			} else {
				this.close();
			}
		}
	},
	computed: {
		mine() {
			return this.userStore.userInfo || {};
		},
		friendName() {
			return this.friend.showNickName || this.friend.nickName || '当前联系人';
		},
		title() {
			let strTitle = `${this.modeText}通话-${this.friendName}`;
			if (this.isChating) {
				strTitle += `(${this.currentTime})`;
			} else if (this.isWaiting) {
				strTitle += `(呼叫中)`;
			}
			return strTitle;
		},
		currentTime() {
			let min = Math.floor(this.videoTime / 60);
			let sec = this.videoTime % 60;
			let strTime = min < 10 ? "0" : "";
			strTime += min;
			strTime += ":"
			strTime += sec < 10 ? "0" : "";
			strTime += sec;
			return strTime;
		},
		isVideo() {
			return this.mode == "video"
		},
		modeText() {
			return this.isVideo ? "视频" : "语音";
		},
		rtcStateText() {
			return this.isChating ? '通话中' : this.isWaiting ? '等待接听' : '正在连接';
		},
		rtcHintText() {
			if (this.state === 'CONNECTING') return '正在建立音视频连接';
			if (this.state === 'STARTING' || this.state === 'ACQUIRING') return '正在打开本地音视频设备';
			if (this.isChating) {
				return `${this.modeText}通话进行中`;
			}
			if (this.isWaiting) {
				return this.isHost ? `等待对方接听${this.modeText}通话` : `等待你接听${this.modeText}通话`;
			}
			return `${this.modeText}通话准备中`;
		},
		remoteStageHintText() {
			if (this.isChating) {
				return '远端画面连接中，稍后会自动展示。';
			}
			return this.rtcHintText;
		},
		quitLabel() {
			return this.isChating ? '挂断通话' : '取消呼叫';
		},
		isChating() {
			return this.state == "CHATING";
		},
		isWaiting() {
			return this.state == "WAITING";
		},
		isClose() {
			return this.state == "CLOSE";
		},
		taskbarStatusText() {
			if (this.isChating) {
				return '通话中';
			} else if (this.isWaiting) {
				return '呼叫中';
			} else {
				return '';
			}
		},
		windowSize() {
			return {
				width: this.isVideo ? 660 : 460,
				height: this.isVideo ? 700 : 500
			}
		}

	},
	mounted() {
		// 初始化音频文件
		this.initAudio();
	},
	created() {
		// 监听页面刷新事件
		window.addEventListener('beforeunload', this.handleBeforeUnload);
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this.handleBeforeUnload);
		this.onQuit();
		if (this.ringtonePlayer) this.ringtonePlayer.destroy();
		this.ringtonePlayer = null;
		this.audio = null;
	}
}
</script>

<style lang="scss">
.rtc-private-video {
	position: relative;
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 12px;
	box-sizing: border-box;
	gap: 12px;
	background: rgba(var(--im-black-rgb), 0.02);

	.rtc-session-strip {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 12px 14px;
		border-radius: 12px;
		background: var(--im-chat-bg);
		border: 1px solid rgba(var(--im-black-rgb), 0.08);
	}

	.session-meta {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
		flex: 1;
	}

	.session-copy {
		min-width: 0;
	}

	.session-name {
		font-size: 14px;
		font-weight: 600;
		line-height: 1.3;
		color: var(--im-text-color-primary);
	}

	.session-hint {
		margin-top: 3px;
		font-size: 12px;
		line-height: 1.4;
		color: var(--im-text-color-light);
	}

	.session-chip {
		flex-shrink: 0;
		height: 24px;
		padding: 0 10px;
		border-radius: 999px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-black-rgb), 0.05);
		color: var(--im-text-color-light);
		font-size: 12px;
		font-weight: 600;

		&.is-active {
			background: rgba(var(--im-success-rgb, 76, 175, 80), 0.12);
			color: var(--im-color-success);
		}
	}

	.rtc-video-box {
		position: relative;
		flex: 1;
		min-height: 0;
		padding: 8px;
		border-radius: 12px;
		background: #18212f;
		border: 1px solid rgba(var(--im-black-rgb), 0.08);
		overflow: hidden;

		.rtc-video-friend {
			position: relative;
			height: 100%;
			border-radius: 10px;
			overflow: hidden;
			background: rgba(var(--im-white-rgb), 0.04);

			.rtc-stage-placeholder {
				position: absolute;
				inset: 0;
				z-index: 1;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 12px;
				background: linear-gradient(180deg, rgba(var(--im-white-rgb), 0.06) 0%, rgba(var(--im-black-rgb), 0.22) 100%);
				color: var(--im-text-color-white);
				text-align: center;
				padding: 20px;
			}

			.placeholder-title {
				font-size: 20px;
				font-weight: 600;
				line-height: 1.3;
			}

			.placeholder-text {
				font-size: 13px;
				line-height: 1.6;
				color: rgba(var(--im-white-rgb), 0.74);
			}

			video {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.rtc-video-mine {
			position: absolute;
			z-index: 2;
			width: 188px;
			height: 132px;
			right: 16px;
			bottom: 16px;
			border-radius: 12px;
			overflow: hidden;
			border: 1px solid rgba(var(--im-white-rgb), 0.18);
			background: rgba(var(--im-black-rgb), 0.3);
			box-shadow: 0 12px 24px -20px rgba(var(--im-black-rgb), 0.56);

			.rtc-mine-placeholder {
				position: absolute;
				inset: 0;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 8px;
				color: rgba(var(--im-white-rgb), 0.78);
				font-size: 12px;
				line-height: 1.4;
				background: rgba(var(--im-black-rgb), 0.36);
				z-index: 1;
			}

			.placeholder-dot {
				width: 8px;
				height: 8px;
				border-radius: 999px;
				background: rgba(var(--im-white-rgb), 0.7);
			}

			video {
				position: relative;
				z-index: 2;
				width: 100%;
				height: 100%;
				object-fit: cover;
				transform: rotateY(180deg);
			}
		}
	}

	.rtc-voice-box {
		flex: 1;
		min-height: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 20px;
		border-radius: 12px;
		background: linear-gradient(180deg, rgba(var(--im-primary-rgb), 0.1) 0%, rgba(var(--im-primary-rgb), 0.04) 100%);
		border: 1px solid rgba(var(--im-primary-rgb), 0.08);

		.voice-card {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
		}

		.voice-avatar-shell {
			padding: 10px;
			border-radius: 999px;
			background: rgba(var(--im-white-rgb), 0.5);
			box-shadow: inset 0 0 0 1px rgba(var(--im-white-rgb), 0.42);
		}

		.rtc-voice-name {
			margin-top: 18px;
			font-size: 22px;
			font-weight: 600;
			line-height: 1.3;
			color: var(--im-text-color-primary);
		}

		.rtc-voice-status {
			margin-top: 8px;
			font-size: 13px;
			line-height: 1.6;
			color: var(--im-text-color-light);
		}
	}

	.rtc-control-bar {
		display: flex;
		justify-content: center;
		align-items: center;
		padding-bottom: 2px;

		.rtc-action-btn {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 10px;
			height: 48px;
			padding: 0 18px;
			border: 0;
			border-radius: 999px;
			font-size: 14px;
			font-weight: 600;
			cursor: pointer;
			transition: transform 0.2s ease, background-color 0.2s ease;

			.icon {
				font-size: 20px;
			}

			&--danger {
				background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.12);
				color: var(--im-color-danger);
			}

			&:hover {
				transform: translateY(-1px);
			}
		}
	}
}
</style>
